import { type FirstTouch, sanitizeFirstTouch, UTM_KEYS } from "./first-touch";

/*
 * First-touch attribution for Apex 25 (same design as theResume's analytics/attribution.ts).
 *
 * - On the first page load in a browser we record the UTMs, the external referrer and the
 *   landing page (sanitised: no query strings, no emails).
 * - tryapex25.com and recruit.theresume.io are different origins, so localStorage is not shared. CTA
 *   links from the marketing site into the app carry the record (plus the marketing-site PostHog
 *   anonymous distinct id) in a single `ft` query parameter; the app adopts it if it has no first touch of
 *   its own yet, then removes `ft` from the address bar.
 * - It is not stored in our database: it lives on the analytics profile (Mixpanel/PostHog super
 *   and set-once person properties) and, from Phase 3, on the CRM lead record for that email.
 */

const STORAGE_KEY = "first_touch";
const DEVICE_KEY = "first_touch_device";
export const HANDOFF_PARAM = "ft";

// Referrers from our own sites are navigation, not acquisition.
const OWN_HOSTS = /(^|\.)(tryapex25\.com|theresume\.io)$/i;

type Handoff = { touch: FirstTouch; device?: string };

const read = (key: string) => {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
};

const write = (key: string, value: string) => {
  try {
    localStorage.setItem(key, value);
  } catch {
    // Private mode / storage disabled: attribution is best effort.
  }
};

const encode = (value: Handoff) =>
  btoa(unescape(encodeURIComponent(JSON.stringify(value))))
    .replace(/\+/g, "-")
    .replace(/\//g, "_")
    .replace(/=+$/, "");

const decode = (value: string): Handoff | null => {
  try {
    const json = decodeURIComponent(escape(atob(value.replace(/-/g, "+").replace(/_/g, "/"))));
    const parsed = JSON.parse(json) as { touch?: unknown; device?: unknown };
    const touch = sanitizeFirstTouch(parsed.touch);
    if (!touch) return null;
    const device =
      typeof parsed.device === "string" && /^[\w$:.-]{1,100}$/.test(parsed.device)
        ? parsed.device
        : undefined;
    return { touch, device };
  } catch {
    return null;
  }
};

export const getFirstTouch = (): FirstTouch | null => {
  const stored = read(STORAGE_KEY);
  if (!stored) return null;
  try {
    return sanitizeFirstTouch(JSON.parse(stored));
  } catch {
    return null;
  }
};

/** Marketing-site PostHog distinct id carried over on a CTA, if any (aliased after identify). */
export const getHandedOffDevice = () => read(DEVICE_KEY) ?? undefined;

const fromCurrentPage = (): FirstTouch | null => {
  const params = new URLSearchParams(window.location.search);
  const raw: Record<string, unknown> = {
    captured_at: new Date().toISOString(),
    landing_page: window.location.pathname,
    landing_host: window.location.host,
  };
  for (const key of UTM_KEYS) raw[key] = params.get(key) ?? undefined;
  try {
    if (document.referrer && !OWN_HOSTS.test(new URL(document.referrer).hostname)) {
      raw.referrer = document.referrer;
    }
  } catch {
    // Malformed referrer: ignore.
  }
  return sanitizeFirstTouch(raw);
};

/**
 * Run once at startup, before anything renders links. Returns the browser's first touch.
 */
export const captureFirstTouch = (): FirstTouch | null => {
  if (typeof window === "undefined") return null;

  const url = new URL(window.location.href);
  const incoming = url.searchParams.get(HANDOFF_PARAM);
  if (incoming !== null) {
    const handoff = decode(incoming);
    // The marketing site saw the visitor first, so its record wins over this page's.
    if (handoff && !getFirstTouch()) write(STORAGE_KEY, JSON.stringify(handoff.touch));
    if (handoff?.device && !read(DEVICE_KEY)) write(DEVICE_KEY, handoff.device);
    // Remove only `ft`, leaving the rest of the query exactly as written (e.g. redirect=/x).
    const rest = window.location.search
      .slice(1)
      .split("&")
      .filter((part) => part && !part.startsWith(`${HANDOFF_PARAM}=`));
    const search = rest.length > 0 ? `?${rest.join("&")}` : "";
    window.history.replaceState(
      window.history.state,
      "",
      `${window.location.pathname}${search}${window.location.hash}`
    );
  }

  const existing = getFirstTouch();
  if (existing) return existing;

  const touch = fromCurrentPage();
  if (touch) write(STORAGE_KEY, JSON.stringify(touch));
  return touch;
};

/** `ft=...` value for a link into the app, or undefined when there is nothing to carry. */
export const handoffValue = (device?: string): string | undefined => {
  const touch = getFirstTouch();
  return touch ? encode({ touch, device }) : undefined;
};

/** Test hook. */
export const decodeHandoff = decode;

const APP_HOST = "recruit.theresume.io";

/**
 * On the marketing hosts: add `ft` to any link into the app at the moment it is used (like GA's
 * linker does for `_gl`), so the handoff carries the current PostHog distinct id and every CTA is
 * covered without each one having to remember.
 */
let decoratorInstalled = false;

export const installHandoffDecorator = (getDevice: () => string | undefined) => {
  if (decoratorInstalled) return;
  decoratorInstalled = true;
  const decorate = (event: Event) => {
    const anchor = (event.target as Element | null)?.closest?.("a[href]");
    if (!(anchor instanceof HTMLAnchorElement)) return;
    try {
      const url = new URL(anchor.href);
      if (url.hostname !== APP_HOST || url.searchParams.has(HANDOFF_PARAM)) return;
      const value = handoffValue(getDevice());
      if (!value) return;
      url.searchParams.set(HANDOFF_PARAM, value);
      anchor.href = url.toString();
    } catch {
      // Not a URL we can decorate.
    }
  };
  // Capture phase and mousedown, so the href is updated before any navigation starts.
  for (const type of ["mousedown", "keydown", "touchstart"]) {
    document.addEventListener(type, decorate, { capture: true, passive: true });
  }
};
